import type { Icon } from '@seech/icons-ng';
import type { NavItem } from '../../models';
import type { WorkspaceBootstrapState, WebChatState } from '../../services';

export interface LayoutRouteContext {
  readonly path: string;
  readonly chatSessionId: string | null;
  readonly hasInvalidChatSessionId: boolean;
  readonly isChatRoute: boolean;
  readonly isHomeRoute: boolean;
}

export interface LayoutNavigationItemViewModel {
  readonly label: string;
  readonly route: string;
  readonly icon: Icon | null;
  readonly isActive: boolean;
}

export interface LayoutProjectViewModel {
  readonly projectId: string;
  readonly label: string;
  readonly isSelected: boolean;
}

export interface LayoutConversationViewModel {
  readonly sessionId: string;
  readonly title: string;
  readonly context: string;
  readonly isActive: boolean;
}

export interface LayoutSidebarViewModel {
  readonly navigation: readonly LayoutNavigationItemViewModel[];
  readonly projects: readonly LayoutProjectViewModel[];
  readonly conversations: readonly LayoutConversationViewModel[];
  readonly isChatReady: boolean;
  readonly isChatLoading: boolean;
  readonly showNewConversation: boolean;
  readonly searchSessionId: string | null;
}

export interface LayoutProfileViewModel {
  readonly displayName: string;
  readonly workspaceLabel: string;
  readonly contextLabel: string;
  readonly avatarUrl: string | null;
  readonly initials: string;
  readonly navigation: readonly LayoutNavigationItemViewModel[];
}

export interface LayoutMobileStatusViewModel {
  readonly workspaceLabel: string;
  readonly isLoading: boolean;
  readonly errorMessage: string | null;
}

export interface LayoutViewModel {
  readonly sidebar: LayoutSidebarViewModel;
  readonly profile: LayoutProfileViewModel;
  readonly mobileStatus: LayoutMobileStatusViewModel;
  readonly isChatRoute: boolean;
}

export type LayoutNavigationCommand = Readonly<{
  kind: 'navigate';
  route: string;
}>;

export type LayoutSidebarCommand =
  | LayoutNavigationCommand
  | Readonly<{ kind: 'toggle-sidebar' }>
  | Readonly<{ kind: 'expand-sidebar' }>
  | Readonly<{ kind: 'close-mobile-navigation' }>
  | Readonly<{ kind: 'create-project' }>
  | Readonly<{ kind: 'select-project'; projectId: string | null }>
  | Readonly<{ kind: 'search-conversations'; sessionId: string }>
  | Readonly<{ kind: 'create-conversation' }>
  | Readonly<{ kind: 'select-conversation'; sessionId: string }>
  | Readonly<{
      kind: 'rename-conversation';
      sessionId: string;
      displayedTitle: string;
    }>
  | Readonly<{ kind: 'archive-conversation'; sessionId: string }>
  | Readonly<{ kind: 'delete-conversation'; sessionId: string }>;

export type LayoutProfileCommand = LayoutNavigationCommand;

export type LayoutMobileStatusCommand = Readonly<{
  kind: 'open-mobile-navigation';
}>;

export type LayoutCommand =
  LayoutSidebarCommand | LayoutProfileCommand | LayoutMobileStatusCommand;

export interface LayoutProjectionInput {
  readonly appState: WorkspaceBootstrapState;
  readonly chatState: WebChatState;
  readonly route: LayoutRouteContext;
  readonly sidebarNavigation: readonly NavItem[];
  readonly menuNavigation: readonly NavItem[];
  readonly now: Date;
}

export function createLayoutRouteContext(url: string): LayoutRouteContext {
  const [pathValue = '/'] = url.split(/[?#]/, 1);
  const path = pathValue.length > 0 ? pathValue : '/';
  const chatPrefix = '/chat/';
  const isChatRoute = path.startsWith(chatPrefix);
  const chatPath = isChatRoute ? path.slice(chatPrefix.length) : '';
  const hasExtraChatPath = chatPath.includes('/');
  let chatSessionId: string | null = null;
  let hasInvalidChatSessionId =
    isChatRoute && (chatPath.length === 0 || hasExtraChatPath);
  if (isChatRoute && chatPath.length > 0 && !hasExtraChatPath) {
    try {
      const decodedChatSessionId = decodeURIComponent(chatPath);
      chatSessionId = readExactLayoutIdentifier(decodedChatSessionId);
      hasInvalidChatSessionId = chatSessionId === null;
    } catch {
      hasInvalidChatSessionId = true;
    }
  }

  return Object.freeze({
    path,
    chatSessionId,
    hasInvalidChatSessionId,
    isChatRoute,
    isHomeRoute: path === '/home' || path === '/',
  });
}

export function projectLayoutViewModel(
  input: LayoutProjectionInput,
): LayoutViewModel {
  const projectIdentifierCounts = countExactIdentifiers(
    input.chatState.projects,
    (project) => project.projectId,
  );
  const projectLabelsById = new Map<string, string>();
  const projects = input.chatState.projects.flatMap((project, index) => {
    const projectId = readExactLayoutIdentifier(project.projectId);
    if (!projectId || projectIdentifierCounts.get(projectId) !== 1) {
      return [];
    }

    const label =
      displayText(project.name) ??
      displayText(project.slug) ??
      `Project ${index + 1}`;
    projectLabelsById.set(projectId, label);

    return [
      Object.freeze({
        projectId,
        label,
        isSelected: projectId === input.chatState.selectedProjectId,
      }),
    ];
  });

  const sessionIdentifierCounts = countExactIdentifiers(
    input.chatState.sessions,
    (session) => session.sessionId,
  );
  const conversations = input.chatState.sessions.flatMap((session, index) => {
    const sessionId = readExactLayoutIdentifier(session.sessionId);
    if (!sessionId || sessionIdentifierCounts.get(sessionId) !== 1) {
      return [];
    }

    const contextParts: string[] = [];
    const projectId = readExactLayoutIdentifier(session.projectId);
    const projectLabel = projectId ? projectLabelsById.get(projectId) : null;
    if (projectLabel) {
      contextParts.push(projectLabel);
    }

    const dateLabel = formatSessionDate(
      session.lastOutboundOn ?? session.lastInboundOn ?? session.createdOn,
      input.now,
    );
    if (dateLabel) {
      contextParts.push(dateLabel);
    }

    return [
      Object.freeze({
        sessionId,
        title: displayText(session.title) ?? `Conversation ${index + 1}`,
        context: contextParts.join(' | '),
        isActive: sessionId === input.route.chatSessionId,
      }),
    ];
  });

  const activeSessionId = readExactLayoutIdentifier(
    input.chatState.session?.sessionId,
  );
  const searchSessionId =
    (input.route.hasInvalidChatSessionId
      ? null
      : (input.route.chatSessionId ??
        activeSessionId ??
        conversations[0]?.sessionId)) ?? null;
  const workspaceLabel = projectWorkspaceLabel(input.appState);
  const profile = projectProfile(input, workspaceLabel);

  return Object.freeze({
    sidebar: Object.freeze({
      navigation: projectNavigation(input.sidebarNavigation, input.route.path),
      projects: Object.freeze(projects),
      conversations: Object.freeze(conversations),
      isChatReady: input.chatState.phase === 'ready',
      isChatLoading: input.chatState.phase === 'bootstrapping',
      showNewConversation: !input.route.isHomeRoute,
      searchSessionId,
    }),
    profile,
    mobileStatus: Object.freeze({
      workspaceLabel,
      isLoading: input.appState.isLoading,
      errorMessage: displayText(input.appState.errorMessage),
    }),
    isChatRoute: input.route.isChatRoute,
  });
}

function projectProfile(
  input: LayoutProjectionInput,
  workspaceLabel: string,
): LayoutProfileViewModel {
  const displayName =
    displayText(input.appState.user?.displayName) ??
    displayText(input.appState.user?.email) ??
    'Operator';
  const roleLabel = input.appState.activeRole ?? 'Member';
  const workspaceTypeLabel = input.appState.activeWorkspaceType;
  const contextLabel = workspaceTypeLabel
    ? `${roleLabel} | ${workspaceTypeLabel}`
    : roleLabel;

  return Object.freeze({
    displayName,
    workspaceLabel,
    contextLabel,
    avatarUrl: displayText(input.appState.user?.avatarUrl),
    initials: projectInitials(displayName),
    navigation: projectNavigation(input.menuNavigation, input.route.path),
  });
}

function projectNavigation(
  navigation: readonly NavItem[],
  currentPath: string,
): readonly LayoutNavigationItemViewModel[] {
  return Object.freeze(
    navigation.flatMap((item) => {
      if (item.route.length === 0) {
        return [];
      }

      return [
        Object.freeze({
          label: displayText(item.label) ?? 'Navigation',
          route: item.route,
          icon: item.icon ?? null,
          isActive: isRouteActive(item.route, currentPath),
        }),
      ];
    }),
  );
}

function projectWorkspaceLabel(state: WorkspaceBootstrapState): string {
  const activeWorkspaceId = readExactLayoutIdentifier(state.activeWorkspaceId);
  if (!activeWorkspaceId) {
    return 'Not selected';
  }

  const matches = state.workspaces.filter(
    (workspace) => workspace.workspaceId === activeWorkspaceId,
  );
  if (matches.length !== 1) {
    return 'Workspace';
  }

  return (
    displayText(matches[0].name) ?? displayText(matches[0].slug) ?? 'Workspace'
  );
}

function projectInitials(displayName: string): string {
  const words = displayName.split(/\s+/).filter((word) => word.length > 0);
  const firstInitial = words[0]?.charAt(0) ?? 'U';
  const secondInitial = words[1]?.charAt(0) ?? words[0]?.charAt(1) ?? '';
  return `${firstInitial}${secondInitial}`.toUpperCase();
}

function formatSessionDate(
  value: string | null | undefined,
  now: Date,
): string {
  if (!value) {
    return '';
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  const elapsedDays = Math.floor(
    (now.getTime() - timestamp) / (1000 * 60 * 60 * 24),
  );
  if (elapsedDays === 0) {
    return 'Today';
  }
  if (elapsedDays === 1) {
    return 'Yesterday';
  }

  const date = new Date(timestamp);
  if (elapsedDays > 1 && elapsedDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function isRouteActive(route: string, currentPath: string): boolean {
  return route === '/'
    ? currentPath === '/'
    : currentPath === route || currentPath.startsWith(`${route}/`);
}

function displayText(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

export function readExactLayoutIdentifier(
  value: string | null | undefined,
): string | null {
  return value && value.length > 0 && value === value.trim() ? value : null;
}

function countExactIdentifiers<T>(
  items: readonly T[],
  selectIdentifier: (item: T) => string | null | undefined,
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const identifier = readExactLayoutIdentifier(selectIdentifier(item));
    if (identifier) {
      counts.set(identifier, (counts.get(identifier) ?? 0) + 1);
    }
  }
  return counts;
}
